// lotProspects.ts — MCC-8a. Lot Sale Monitor queue client. The raw ArcGIS-
// ingested buffer: list/filter, detail, soft-delete, manual run-ingestion.
// AI scoring + convert-to-profile are MCC-8b.

import { request } from './request'

export interface LotProspectListRow {
  id: string
  parcel_id: string
  sale_date: string
  owner_name: string | null
  owner_mailing_address: string | null
  lot_address: string | null
  city: string | null
  zip: string | null
  is_priority_city: number
  sale_price: number | null
  lot_size_acres: number | null
  prospect_score: number | null
  estimated_pricing_tier: string | null
  owner_type: string | null
  is_developer_batch: number
  outreach_status: string
  profile_id: string | null
  converted_at: string | null
  deleted_at: string | null
  created_at: string
}

export interface LotProspectListResponse {
  ok: boolean
  prospects: LotProspectListRow[]
  total: number
  limit: number
}

export interface LotProspectFilters {
  outreach_status?: string
  min_score?: number
  priority_city_only?: boolean
  city?: string
  search?: string
  include_deleted?: boolean
}

export interface IngestionResult {
  ok: boolean
  scanned: number
  inserted: number
  updated: number
  skipped: number
  since: string
  error?: string
}

function toQuery(f: LotProspectFilters): string {
  const p = new URLSearchParams()
  if (f.outreach_status) p.set('outreach_status', f.outreach_status)
  if (typeof f.min_score === 'number') p.set('min_score', String(f.min_score))
  if (f.priority_city_only) p.set('priority_city_only', 'true')
  if (f.city) p.set('city', f.city)
  if (f.search) p.set('search', f.search)
  if (f.include_deleted) p.set('include_deleted', 'true')
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const lotProspectsApi = {
  list: (filters: LotProspectFilters = {}) =>
    request<LotProspectListResponse>(`/api/admin/lot-prospects${toQuery(filters)}`),

  get: (id: string) =>
    request<{ ok: boolean; prospect: Record<string, unknown> }>(
      `/api/admin/lot-prospects/${encodeURIComponent(id)}`,
    ),

  remove: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/lot-prospects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Manual scan. No body = fetch ArcGIS from the default rolling window.
  runIngestion: (input?: { since?: string; max_pages?: number }) =>
    request<IngestionResult>('/api/admin/lot-prospects/run-ingestion', {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    }),

  // MCC-8b: AI-score unscored prospects (Lane B; degrades ok:false if off).
  score: (input?: { limit?: number }) =>
    request<{ ok: boolean; reason?: string; scored: number; failed: number; candidates: number }>(
      '/api/admin/lot-prospects/score',
      { method: 'POST', body: JSON.stringify(input ?? {}) },
    ),

  // MCC-8b: convert a prospect into a profile lead (the human "pursue" gate).
  convert: (id: string) =>
    request<{ ok: boolean; profile_id: string; created_profile: boolean; already_converted?: boolean }>(
      `/api/admin/lot-prospects/${encodeURIComponent(id)}/convert`,
      { method: 'POST' },
    ),

  // MCC-8b: triage (status / sender / notes / mail timestamps / response).
  patch: (id: string, input: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/api/admin/lot-prospects/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
}
